import { ProductType, PurchasableProduct } from '@accedo/one-plugin-sdk';

import { PackageDefinition } from '#/client/models';
import { PickerItemProps } from '#/components/Picker/PickerItem';
import { translateDescription, translatePrice } from '#/plugins/paywall/utils';
import { paywallActions } from '#/store/paywall/paywall.store';

/** Divider item (visual separator between groups; optional tabId for tab filtering when tabs are used) */
export interface ProductPickerDivider {
  type: 'divider';
  id: string;
  tabId?: string;
}

/** Product item (selectable) or divider */
export type ProductPickerItem = PickerItemProps | ProductPickerDivider;

export const isProductPickerDivider = (
  item: ProductPickerItem
): item is ProductPickerDivider => 'type' in item && item.type === 'divider';

interface OrderItem {
  typeOrder: number;
  groupOrder: number;
  tierOrder: number;
  productOrder: number;
  productIdentifier: string;
  groupName: string | undefined;
  promotionalLabel?: string;
  description?: string;
  name?: string;
}

const getSafeId = (id: string | undefined) =>
  paywallActions.transformProductIdentifierSafely(id);

export const getPickerItems = (
  products: PurchasableProduct[],
  packageDefinition: PackageDefinition
): ProductPickerItem[] => {
  const { subscriptionGroups, oneTimePurchases } = packageDefinition;
  const productById = Object.fromEntries(
    products.map((product) => [getSafeId(product.id), product])
  );

  let prev: OrderItem;

  const order: OrderItem[] = subscriptionGroups
    // add "plans" from all the groups and tiers
    .flatMap<OrderItem>((group, groupIndex) =>
      group.tiers
        .filter((tier) => tier.purchasable)
        .flatMap((tier) =>
          tier.plans
            .filter(
              (plan) =>
                plan.purchasable &&
                getSafeId(plan.web?.productIdentifier) in productById
            )
            .map((plan, planIndex) => ({
              typeOrder: 0,
              groupOrder: groupIndex,
              tierOrder: tier.displayOrder,
              productOrder: planIndex,
              productIdentifier: getSafeId(plan.web?.productIdentifier),
              groupName: group.name,
              promotionalLabel: plan.promotionalLabel,
              description: plan.description,
              name: plan.name,
            }))
        )
    )
    // add "oneTimePurchases"
    .concat(
      oneTimePurchases
        .filter(
          (oneTimePurchase) =>
            getSafeId(oneTimePurchase.web?.productIdentifier) in productById
        )
        .map((product, productIndex) => ({
          typeOrder:
            productById[getSafeId(product.web?.productIdentifier)]?.type ===
            ProductType.Consumable
              ? 1
              : 2,
          groupOrder: 0,
          tierOrder: 0,
          productOrder: productIndex,
          productIdentifier: getSafeId(product.web?.productIdentifier),
          groupName: undefined,
          promotionalLabel: product.promotionalLabel,
          description: product.description,
          name: product.name,
        }))
    )
    // sort based on all the order fields
    .sort(
      (a, z) =>
        a.typeOrder - z.typeOrder ||
        a.groupOrder - z.groupOrder ||
        a.tierOrder - z.tierOrder ||
        a.productOrder - z.productOrder
    );

  // Build a flat list: product items and dividers (with tabId for tab filtering)
  return order.flatMap((item) => {
    const pickerItems: ProductPickerItem[] = [];
    const product = productById[getSafeId(item.productIdentifier)];

    if (
      prev !== undefined &&
      prev.groupOrder !== item.groupOrder &&
      item.groupName
    ) {
      pickerItems.push({
        type: 'divider',
        id: `divider-${item.groupOrder}-${item.groupName}`,
        tabId: product.type,
      });
    }

    const { title, description, subtitle, detail } =
      getPickerItemPropsFromProduct(product, item.description);

    pickerItems.push({
      id: product.id,
      title,
      description,
      subtitle,
      detail,
      tabId: product.type,
      promotionalLabel: item.promotionalLabel,
    });

    prev = item;

    return pickerItems;
  });
};

export const getPickerItemPropsFromProduct = (
  product: PurchasableProduct,
  packageDefinitionDescription?: string
): PickerItemProps => {
  const { type, id, title, description, price, currency } = product;

  const nextPayment =
    type === ProductType.Subscription ? product.nextPayment : undefined;

  const duration =
    type === ProductType.Subscription || type === ProductType.Consumable
      ? product.duration
      : undefined;

  return {
    id,
    title,
    description: packageDefinitionDescription
      ? translateDescription(
          packageDefinitionDescription,
          price,
          currency,
          nextPayment
        )
      : undefined,
    subtitle: translateDescription(description, price, currency, nextPayment),
    detail: translatePrice(price, currency, duration, nextPayment),
  };
};
